import { GatewayRepository } from '../repositories/gateway.repository'
import {
    CreateClientPayloadT,
    CreateMicroservicePayloadT,
    CreatePermissionPayloadT,
    CreateRolePayloadT,
    GatewayGetClientsResponseT,
    GatewayGetMicroservicesResponseT,
    GatewayGetPermissionsResponseT,
    GatewayGetRolesResponseT,
    GatewayGetScopeRoutesResponseT,
    GatewayGetScopesResponseT,
    GatewayGetRoutesResponseT,
    GatewayRouteT,
    GatewayGetScopesParamsT,
    GatewayGetRoutesParamsT,
    RolesListParamsT,
    ClientsListParamsT,
    MicroservicesListParamsT,
    GatewayGenerateTokenResponseT,
} from '../types/gateway.types'

const gatewayRepository = new GatewayRepository()

export class GatewayService {
    async getRoles(page: number, payload?: RolesListParamsT): Promise<GatewayGetRolesResponseT> {
        const response = await gatewayRepository.getRoles(page, payload)
        return response.data
    }

    async getPermissions(
        page: number,
        name?: string,
        serviceId?: string
    ): Promise<GatewayGetPermissionsResponseT> {
        const response = await gatewayRepository.getPermissions(page, name, serviceId)
        return response.data
    }

    async getMicroservices(
        page: number,
        search?: string,
        payload?: MicroservicesListParamsT
    ): Promise<GatewayGetMicroservicesResponseT> {
        const response = await gatewayRepository.getMicroservices(page, search, payload)
        return response.data
    }

    async getScopes(
        page: number,
        params?: GatewayGetScopesParamsT
    ): Promise<GatewayGetScopesResponseT> {
        const response = await gatewayRepository.getScopes(page, params)
        return response.data
    }

    async getRoutes(
        page: number,
        params?: GatewayGetRoutesParamsT
    ): Promise<GatewayGetRoutesResponseT> {
        const response = await gatewayRepository.getRoutes(page, params)
        return response.data
    }

    async getScopeRoutes(scope_id: number, page: number): Promise<GatewayGetScopeRoutesResponseT> {
        const response = await gatewayRepository.getScopeRoutes(scope_id, page)
        return response.data
    }

    async linkRoute(scope_id: number, route_id: number): Promise<void> {
        const response = await gatewayRepository.linkRoute(scope_id, route_id)
        return response.data
    }

    async unlinkRoute(scope_id: number, route_id: number): Promise<void> {
        const response = await gatewayRepository.unlinkRoute(scope_id, route_id)
        return response.data
    }

    async createScope(scope: string, description: string, isActive: boolean): Promise<void> {
        const response = await gatewayRepository.createScope(scope, description, isActive)
        return response.data
    }

    async createRoute(route: GatewayRouteT): Promise<void> {
        const response = await gatewayRepository.createRoute(route)
        return response.data
    }

    async updateScope(
        scope_id: number,
        scope: string,
        description: string,
        isActive: boolean
    ): Promise<void> {
        const response = await gatewayRepository.updateScope(scope_id, scope, description, isActive)
        return response.data
    }

    async createMicroservice(payload: CreateMicroservicePayloadT): Promise<void> {
        const response = await gatewayRepository.createMicroservice(payload)
        return response.data
    }

    async updateMicroservice(
        microservice_id: number,
        payload: CreateMicroservicePayloadT
    ): Promise<void> {
        const response = await gatewayRepository.updateMicroservice(microservice_id, payload)
        return response.data
    }

    async importAllRoutes(): Promise<void> {
        const response = await gatewayRepository.importAllRoutes()
        return response.data
    }

    async importMicroserviceRoutes(microservice_id: number): Promise<void> {
        const response = await gatewayRepository.importMicroserviceRoutes(microservice_id)
        return response.data
    }

    async createPermission(payload: CreatePermissionPayloadT): Promise<void> {
        const response = await gatewayRepository.createPermission(payload)
        return response.data
    }

    async updatePermission(
        permission_id: number,
        payload: CreatePermissionPayloadT
    ): Promise<void> {
        const response = await gatewayRepository.updatePermission(permission_id, payload)
        return response.data
    }

    async createRole(payload: CreateRolePayloadT): Promise<void> {
        const response = await gatewayRepository.createRole(payload)
        return response.data
    }

    async updateRole(role_id: number, payload: CreateRolePayloadT): Promise<void> {
        const response = await gatewayRepository.updateRole(role_id, payload)
        return response.data
    }

    async updateRoute(route: GatewayRouteT): Promise<void> {
        const response = await gatewayRepository.updateRoute(route)
        return response.data
    }

    async deleteRole(role_id: number): Promise<void> {
        const response = await gatewayRepository.deleteRole(role_id)
        return response.data
    }

    async deleteRoute(route_id: number): Promise<void> {
        const response = await gatewayRepository.deleteRoute(route_id)
        return response.data
    }

    async getClients(
        page: number,
        payload?: ClientsListParamsT
    ): Promise<GatewayGetClientsResponseT> {
        const response = await gatewayRepository.getClients(page, payload)
        return response.data
    }

    async createClient(payload: CreateClientPayloadT): Promise<void> {
        const response = await gatewayRepository.createClient(payload)
        return response.data
    }

    async updateClient(client_id: number, payload: CreateClientPayloadT): Promise<void> {
        const response = await gatewayRepository.updateClient(client_id, payload)
        return response.data
    }

    async deleteClient(client_id: number): Promise<void> {
        const response = await gatewayRepository.deleteClient(client_id)
        return response.data
    }

    async deletePermission(id: number): Promise<void> {
        const response = await gatewayRepository.deletePermission(id)
        return response.data
    }

    async generateToken(
        user_id: number,
        client_id: number
    ): Promise<GatewayGenerateTokenResponseT> {
        const response = await gatewayRepository.generateToken(user_id, client_id)
        return response.data
    }
}
